import { decodeHtmlEntities } from "./decode-html-entities";
import { htmlToPlainText } from "./html-to-plain-text";

/**
 * remark plugin for Markdown that is about to be flattened into a preview:
 * `MarkdownRender inline` (cards) and `markdownToPlainText` (clamped lines).
 * Both unwrap block structure, which on its own loses things:
 *
 *  - List markers. `2024. A great year` or `1. Arrive early` parses as a
 *    list, and unwrapping `<ol>/<li>` keeps the text but drops the number —
 *    content, not formatting, in a short description. An ordered item gets its
 *    original marker (`2024.`, `3)`) back from the source; a bullet item gets
 *    `•` (or `☑`/`☐` for a task item), so unwrapped items don't run together.
 *    Bullet items with no visible text (empty, or only an image or `&nbsp;`)
 *    are dropped.
 *  - Indented pastes and code spans. CommonMark doesn't decode entities in
 *    code, so a pasted `&nbsp;` would show literally once the code formatting
 *    is unwrapped. An unfenced code block becomes a paragraph of its text and
 *    a code span becomes text, entities decoded. A fenced block is deliberate
 *    code and keeps its text as written.
 *  - Raw HTML. Without `rehype-raw` it would print as literal tags. Tags are
 *    dropped and the text between them kept; comments, `<script>` and
 *    `<style>` go entirely.
 *  - Footnotes. GFM's footnote section has a screen-reader-only "Footnotes"
 *    heading and `↩` back-links that turn into visible text once unwrapped.
 *    References and definitions are dropped instead.
 */

interface MdNode {
  type: string;
  value?: string;
  ordered?: boolean | null;
  start?: number | null;
  checked?: boolean | null;
  children?: MdNode[];
  position?: { start?: { offset?: number } };
}

// A list item starts at its marker: up to 9 digits then `.` or `)`.
const ORDERED_MARKER = /^(\d{1,9}[.)])/;
// A fenced block's position starts at its fence (the up-to-3-space indent is
// already consumed); an indented block's starts at its indentation. So a
// fence is only a fence when it is the very first thing.
const FENCE = /^(`{3,}|~{3,})/;
const BR = /^<br\s*\/?>$/i;

function htmlToText(node: MdNode, inline: boolean): MdNode | null {
  const html = node.value ?? "";
  // `a<br>b` must not glue into `ab`.
  if (BR.test(html)) return { type: "text", value: " " };
  const text = htmlToPlainText(html);
  if (!text) return null;
  return inline ? { type: "text", value: text } : { type: "paragraph", children: [{ type: "text", value: text }] };
}

function sourceAt(node: MdNode, source: string, length: number): string {
  const offset = node.position?.start?.offset;
  return offset === undefined ? "" : source.slice(offset, offset + length);
}

function orderedMarker(item: MdNode, source: string, fallback: number): string {
  return ORDERED_MARKER.exec(sourceAt(item, source, 10))?.[1] ?? `${fallback}.`;
}

function prefix(item: MdNode, marker: string): void {
  const text: MdNode = { type: "text", value: `${marker} ` };
  const first = item.children?.[0];
  if (first?.type === "paragraph") {
    first.children = [text, ...(first.children ?? [])];
  } else {
    // An empty item (`1.` alone) or one opening with a non-paragraph block.
    item.children = [{ type: "paragraph", children: [text] }, ...(item.children ?? [])];
  }
}

// Parents whose children are blocks; anywhere else an `html` node is inline.
const BLOCK_CONTAINERS = new Set(["root", "blockquote", "listItem"]);

function flattenChild(child: MdNode, source: string, parent: MdNode): MdNode | null {
  switch (child.type) {
    case "footnoteDefinition":
    case "footnoteReference":
      return null;
    case "html":
      return htmlToText(child, !BLOCK_CONTAINERS.has(parent.type));
    case "inlineCode":
      return { type: "text", value: decodeHtmlEntities(child.value) };
    case "code":
      if (FENCE.test(sourceAt(child, source, 3))) return child;
      return {
        type: "paragraph",
        children: [{ type: "text", value: decodeHtmlEntities(child.value) }],
      };
  }
  return child;
}

// Inline, `<script>x</script>` parses as tag, text, tag: drop what's between.
// Only when the closing tag really follows: text that merely mentions
// `<style>` must keep the rest of its sentence (the tag itself drops like
// any other).
const HIDDEN_OPEN = /^<(script|style)\b(?![\s\S]*<\/\1\s*>)/i;

function withoutHiddenInlineContent(children: MdNode[]): MdNode[] {
  const kept: MdNode[] = [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i]!;
    kept.push(child);
    const open = child.type === "html" ? HIDDEN_OPEN.exec(child.value ?? "") : null;
    if (!open) continue;
    const closing = new RegExp(`</${open[1]}\\s*>`, "i");
    const end = children.findIndex(
      (next, j) => j > i && next.type === "html" && closing.test(next.value ?? ""),
    );
    if (end !== -1) i = end;
  }
  return kept;
}

function visit(node: MdNode, source: string): void {
  if (!node.children) return;
  node.children = withoutHiddenInlineContent(node.children)
    .map((child) => flattenChild(child, source, node))
    .filter((child): child is MdNode => child !== null);
  for (const child of node.children) visit(child, source);

  if (node.type !== "list") return;
  const items = node.children;
  // A bullet with no visible text (`- ` alone, or only an image or `&nbsp;`,
  // which previews can't show) would leave a lone marker: `•` as a whole
  // meta description. Empty ordered items stay: a lone `2024.` is content,
  // and keeps its number.
  if (!node.ordered) node.children = items.filter(hasText);
  node.children.forEach((item) => {
    const index = items.indexOf(item);
    prefix(item, node.ordered ? orderedMarker(item, source, (node.start ?? 1) + index) : bulletMarker(item));
  });
}

// Whether a node shows any text once flattened. Runs after its children are
// flattened, so raw HTML is already its text; images never show.
function hasText(node: MdNode): boolean {
  if (node.type === "image" || node.type === "imageReference") return false;
  if (node.type === "text" || node.type === "inlineCode" || node.type === "code") {
    return /\S/.test(node.value ?? "");
  }
  return (node.children ?? []).some(hasText);
}

// A task item's checkbox is dropped when flattened, so its state moves into
// the marker: a bare `•` would make `- [ ] Parking` read as included.
function bulletMarker(item: MdNode): string {
  if (item.checked === true) return "☑";
  if (item.checked === false) return "☐";
  return "•";
}

export function remarkFlattenForPreview() {
  return (tree: MdNode, file: { value?: unknown }) => {
    visit(tree, String(file.value ?? ""));
  };
}
